import { cn } from '../../utils/utils';

import { ProgressBar } from './ProgressBar';

export interface StepProgressStep {
  id: string;
  label: string;
}

export interface StepProgressProps {
  /** In order. Naming the steps is the point — see the note below. */
  steps: readonly StepProgressStep[];
  /** Zero-based index of the step the user is on. */
  currentIndex: number;
  /** The progress bar's accessible name. */
  ariaLabel?: string;
  className?: string;
}

/**
 * Position in a multi-step flow: a counter, a bar, and the step names.
 *
 * Naming the steps is not decoration. "Step 2 of 4" says how far along you are
 * but not what is coming, and "am I about to hit a paywall?" is a real reason
 * to abandon a flow. Seeing what is ahead — and that it ends — answers that up
 * front, which is why this renders the labels rather than just a fraction.
 *
 * Three renderings of one fact, split by audience: the counter and the stepper
 * row are for sighted users, and the single `progressbar` carries the whole
 * thing in its accessible name for everyone else. The visual halves are
 * `aria-hidden` precisely because they are duplicates — exposing all three
 * announced the position three times in a row.
 */
export function StepProgress({
  steps,
  currentIndex,
  ariaLabel = 'Progress',
  className,
}: StepProgressProps) {
  const total = steps.length;
  // Clamped so an out-of-range index renders a sane bar instead of crashing on
  // the label lookup below.
  const index = Math.min(Math.max(currentIndex, 0), total - 1);
  const position = index + 1;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {/* `font-mono` — the label/numeral register (see tailwind.config). */}
      <div
        aria-hidden='true'
        className='text-eyebrow text-muted-foreground text-center font-mono uppercase'
      >
        Step {position} of {total}
      </div>

      {/* `value = position` against a min of 0, so step 1 of 4 fills a quarter
          rather than nothing — arriving on a flow that reads as 0% complete
          makes the first screen feel like it doesn't count. The visible fill
          and `aria-valuenow` therefore agree; the wizard's hand-rolled bar drew
          25% while announcing valuemin=1/valuenow=1, i.e. 0%. */}
      <ProgressBar
        value={position}
        max={total}
        size='sm'
        ariaLabel={ariaLabel}
        ariaValueText={`Step ${position} of ${total}: ${steps[index].label}`}
      />

      <ol aria-hidden='true' className='text-label flex justify-between'>
        {steps.map((step, i) => (
          <li
            key={step.id}
            className={
              i <= index
                ? 'text-brand-700 dark:text-brand-300'
                : 'text-muted-foreground'
            }
          >
            {step.label}
          </li>
        ))}
      </ol>
    </div>
  );
}
